import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import applicationService from '../services/applicationService';
import StatusBadge from '../components/StatusBadge';

const AdminDocumentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('');

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await applicationService.getApplications();
      if (response.success) {
        setApplications(response.applications);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = !searchTerm || 
      app.jobPosting.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.candidateId?.firstName + ' ' + app.candidateId?.lastName).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStage = !filterStage || app.currentStage === filterStage;
    
    return matchesSearch && matchesStage;
  });

  const stages = [...new Set(applications.map(app => app.currentStage))];

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 max-w-[1400px] mx-auto">
          <div className="flex flex-col items-center justify-center py-20 px-5">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
            <p>Loading applications...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="mb-8 pb-4 border-b-2 border-gray-200">
          <div>
            <h1 className="m-0 mb-2 text-gray-800 text-3xl">Document Management</h1>
            <p className="m-0 text-gray-600 text-base">View and manage all application documents</p>
          </div>
        </div>

        <div className="flex gap-4 mb-6 flex-wrap">
          <div className="flex-1 min-w-[250px]">
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-md text-base bg-white focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)]"
              placeholder="Search by job posting or candidate name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex-1 min-w-[250px]">
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-md text-base bg-white focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)]"
              value={filterStage}
              onChange={(e) => setFilterStage(e.target.value)}
            >
              <option value="">All Stages</option>
              {stages.map(stage => (
                <option key={stage} value={stage}>
                  {stage.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 text-red-900 px-4 py-4 rounded-md mb-6 border border-red-200">{error}</div>
        )}

        {filteredApplications.length === 0 ? (
          <div className="text-center py-20 px-5 bg-white rounded-lg shadow-sm">
            <div className="text-6xl mb-4">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline-block">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <h2 className="text-gray-800 mb-2">No Applications Found</h2>
            <p className="text-gray-600">No applications match your search criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-5 mb-8">
            {filteredApplications.map(app => (
              <div key={app._id} className="bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
                <div className="p-5 bg-gray-50 border-b border-gray-200 flex justify-between items-start gap-3">
                  <div>
                    <h3 className="m-0 mb-2 text-gray-800 text-lg">{app.jobPosting}</h3>
                    {app.candidateId && (
                      <p className="m-0 text-gray-600 text-sm">
                        {app.candidateId.firstName} {app.candidateId.lastName}
                      </p>
                    )}
                  </div>
                  <StatusBadge stage={app.currentStage} />
                </div>
                
                <div className="p-5">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 text-sm">Applied:</span>
                    <span className="text-gray-800 font-medium text-sm">
                      {new Date(app.appliedAt).toLocaleDateString()}
                    </span>
                  </div>
                  {app.candidateId?.phoneNumber && (
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600 text-sm">Phone:</span>
                      <span className="text-gray-800 font-medium text-sm">{app.candidateId.phoneNumber}</span>
                    </div>
                  )}
                </div>

                <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">
                  <button
                    className="w-full px-4 py-2.5 bg-blue-600 text-white border-0 rounded-md text-[0.95rem] font-medium cursor-pointer transition-colors duration-200 hover:bg-blue-800"
                    onClick={() => navigate(`/applications/${app._id}`)}
                  >
                    View Documents & Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-5 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm flex items-center gap-4">
            <div className="text-4xl">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
              </svg>
            </div>
            <div>
              <h3 className="m-0 mb-2 text-gray-600 text-sm font-medium">Total Applications</h3>
              <p className="m-0 text-gray-800 text-3xl font-bold">{applications.length}</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm flex items-center gap-4">
            <div className="text-4xl">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <div>
              <h3 className="m-0 mb-2 text-gray-600 text-sm font-medium">Filtered Results</h3>
              <p className="m-0 text-gray-800 text-3xl font-bold">{filteredApplications.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-600">
          <h3 className="m-0 mb-4 text-gray-800 text-lg"> Quick Guide</h3>
          <ul className="m-0 pl-5">
            <li className="mb-2 text-gray-700 leading-relaxed"><strong className="text-gray-800">View Documents:</strong> Click "View Documents & Details" on any application</li>
            <li className="mb-2 text-gray-700 leading-relaxed"><strong className="text-gray-800">Verify Documents:</strong> {user?.role === 'ADMIN' ? 'You can verify/reject documents' : 'Only ADMIN can verify/reject documents'}</li>
            <li className="mb-2 text-gray-700 leading-relaxed"><strong className="text-gray-800">Search:</strong> Use the search box to find specific applications</li>
            <li className="mb-2 text-gray-700 leading-relaxed"><strong className="text-gray-800">Filter:</strong> Filter by application stage</li>
          </ul>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminDocumentsPage;
